"use client";

import styles from "./charts.module.css";
import { useChartFrame } from "./useChartFrame";

export type BarRow = {
  label: string;
  /** Valor de la barra (en la escala de `max`). */
  value: number;
  /** Texto del valor, ya formateado en español (10 %, 4,25). */
  display: string;
  /** Resalta la fila con el acento (nuestro modelo, la deliberación). */
  accent?: boolean;
};

/**
 * Barras horizontales en HTML para comparaciones simples de proporciones (gráficos 2 y
 * 4 de la guía, sección 11). En HTML y no en SVG: el texto se lee a su tamaño en
 * cualquier ancho y la lista ya es su propia tabla accesible.
 */
export function BarsChart({
  title,
  rows,
  max,
  caption,
  fine,
  id,
}: {
  title: string;
  rows: BarRow[];
  max: number;
  caption?: string;
  fine?: string;
  id: string;
}) {
  const { ref, inView } = useChartFrame<HTMLDivElement>(640);
  const titleId = `${id}-titulo`;
  return (
    <figure id={id} className={styles.chart} data-inview={inView ? "true" : undefined} aria-labelledby={titleId}>
      <div className={styles.header}>
        <p id={titleId} className={styles.title}>
          {title}
        </p>
      </div>
      <div ref={ref}>
        <ul className={styles.rows}>
          {rows.map((r, i) => (
            <li key={r.label} className={styles.row}>
              <div className={styles.rowHead}>
                <span className={styles.rowLabel}>{r.label}</span>
                <span className={styles.rowValue}>
                  <strong>{r.display}</strong>
                </span>
              </div>
              <div className={styles.track} aria-hidden="true">
                <span
                  className={`${styles.fill} ${styles.grow}`}
                  data-order={String(Math.min(i, 3))}
                  style={{ width: `${(r.value / max) * 100}%`, background: r.accent ? "var(--c-accent)" : undefined }}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
      {(caption || fine) && (
        <figcaption className={styles.fine}>
          {caption && <p>{caption}</p>}
          {fine && (
            <p>
              <em>{fine}</em>
            </p>
          )}
        </figcaption>
      )}
    </figure>
  );
}
